import { z } from 'zod'
import { oc } from '@orpc/contract'
import { practitionerRowSchema } from './list-practitioners.contract'
import { practitionerPhotoSchema } from './member-credentials.contract'

/**
 * The signed-in Org Member as My Profile shows them (see the glossary): the Provider
 * Operations row minus the integration mappings (DoseSpot, Bridge, Stedi are a
 * coordinator's concern, never the member's own), plus what the summary card and the
 * Personal information tab need.
 */
export const myProfileSchema = practitionerRowSchema
  .omit({
    doseSpotClinicianId: true,
    doseSpotClinicianKind: true,
    bridgeProviderId: true,
    stediProviderId: true,
  })
  .extend({
    // `null` when none is stored, as on the get-one response.
    photo: practitionerPhotoSchema.nullish(),
    // The member's first non-temp phone (a `temp` phone is 2FA enrollment's, not theirs).
    mobilePhone: z.string().nullable(),
    // The console's own label — Physician, Care Coordinator, Call Center Manager, Healthcare
    // Service Manager, Super Admin — Physician winning when several apply (the audit actor
    // precedence). Resolved here so the console mirrors nothing.
    roleLabel: z.string(),
    // The names of the Organizations of every active PractitionerRole; empty for a super
    // admin, who holds none.
    organizations: z.array(z.string()),
    // True when any active role carries the Medical doctor code: the Professional details
    // tab exists exactly then.
    isClinical: z.boolean(),
  })

export type MyProfileDto = z.infer<typeof myProfileSchema>

/**
 * The caller's own record for My Profile. No input: the member is the session's profile,
 * never a client-supplied id. Reads run on the admin client so the answer never depends on
 * the caller's access policy. A profile that is not a Practitioner gets 403.
 */
export const getMyProfile = oc
  // `me/…`, like `me/scope`: a one-segment literal under `/v2/practitioners/` would be
  // matched by `GET /v2/practitioners/{practitionerId}` with "profile" as the id.
  .route({ method: 'GET', path: '/v2/practitioners/me/profile' })
  .output(myProfileSchema)

// E.164 as the OTP sender accepts it; the console sends `+1` and ten digits.
const E164_PHONE = /^\+[1-9]\d{7,14}$/

/**
 * What the member may change on their own record. Every field is tri-state where it can
 * be: present replaces, `null` clears, absent leaves — so a photo save from the summary card
 * re-sends neither names nor phone, and a phone stored in an older format stays as it is
 * until edited. A name, when present, is trimmed and non-empty: it can be replaced, never
 * cleared.
 */
export const updateMyProfileInputSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').optional(),
  lastName: z.string().trim().min(1, 'Last name is required').optional(),
  mobilePhone: z
    .string()
    .regex(E164_PHONE, 'mobile phone must be E.164, like +15551234567')
    .nullish(),
  photo: practitionerPhotoSchema.nullish(),
})

export type UpdateMyProfileInput = z.infer<typeof updateMyProfileInputSchema>

/**
 * The caller's own names, mobile phone and photo. The member is the session's profile, as
 * on the read; the write runs on the admin client. A profile that is not a Practitioner
 * gets 403.
 */
export const updateMyProfile = oc
  .route({ method: 'POST', path: '/v2/practitioners/me/profile' })
  .input(updateMyProfileInputSchema)
  .output(z.object({ practitionerId: z.string() }))
